import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { site } from "@/lib/site";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      ...routeMeta("/disclaimer"),
      { title: "Disclaimer — CCOB And Company PA" },
      { name: "description", content: "Disclaimer regarding the use of information provided on the CCOB And Company PA website." },
      { property: "og:title", content: "Disclaimer — CCOB And Company PA" },
      { property: "og:description", content: "Important information about the content on this website." },
    ],
    links: canonicalLinks("/disclaimer"),
  }),
  component: DisclaimerPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-10">
      <h2 className="font-display text-2xl text-primary">{title}</h2>
      <div className="gold-line mt-3" />
      <div className="mt-4 text-foreground/80 leading-relaxed space-y-3 text-[15px]">{children}</div>
    </div>
  );
}

function DisclaimerPage() {
  return (
    <>
      <PageHero eyebrow="Legal" title="Disclaimer" subtitle="Please read before relying on the information published on this website." image={img.heroTerms} />
      <section className="py-20">
        <div className="container-x max-w-3xl">
          <p className="text-sm text-muted-foreground uppercase tracking-widest">Last updated: May 2026</p>

          <Section title="General Information Only">
            <p>The information presented on this website is for general informational purposes only and does not constitute professional accounting, tax, financial, or legal advice. Tax laws and regulations change frequently and vary by jurisdiction; the content here may not reflect the most current developments.</p>
          </Section>

          <Section title="No Client Relationship">
            <p>Reading this website, downloading materials, submitting a contact form, or otherwise interacting with the site does not create an accountant-client relationship between you and {site.name}. A formal engagement begins only after a written agreement is signed by both parties.</p>
          </Section>

          <Section title="Consult a Professional">
            <p>You should not act or refrain from acting on the basis of any content on this site without first seeking advice from a qualified CPA, attorney, or other professional who has reviewed your specific situation. Every taxpayer's circumstances are different.</p>
          </Section>

          <Section title="Calculators & Tools">
            <p>Any calculators, estimators, or tools provided on this site are for illustrative purposes only. Results are estimates based on the inputs you provide and the assumptions built into the tool, and they should not be used as the sole basis for any financial or tax decision.</p>
          </Section>

          <Section title="External Links">
            <p>This website contains links to third-party sites for your convenience. We do not control and are not responsible for the content, accuracy, or privacy practices of any external website. Inclusion of a link does not imply endorsement.</p>
          </Section>

          <Section title="Limitation of Liability">
            <p>To the fullest extent permitted by law, {site.name} disclaims all liability for any direct, indirect, incidental, or consequential damages arising from your use of, or reliance on, the information contained on this website.</p>
          </Section>

          <Section title="Questions">
            <p>For specific advice tailored to your situation, please call our office at {site.phone} or <a href="/contact#consultation" className="text-gold hover:underline">send a secure message</a> to schedule a consultation.</p>
          </Section>
        </div>
      </section>
    </>
  );
}
